// /src/pages/private/admin/membersPerks/profileThemes/ThemeLivePreview.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../authContext';
import { fetchMemberProfileCard } from '../../../../../components/tools/memberProfileCardApi';
import DonorShootingStar from '../../../../../components/tools/memberProfileCard/DonorShootingStar';
import PerkThemeMediaLayer from '../../../../../components/tools/memberProfileCard/PerkThemeMediaLayer';
import PerkThemeHaloLayer from '../../../../../components/tools/memberProfileCard/PerkThemeHaloLayer';
import PerkThemeOverlayEffects from '../../../../../components/tools/memberProfileCard/PerkThemeOverlayEffects';
import PerkThemeUnderlayEffects from '../../../../../components/tools/memberProfileCard/PerkThemeUnderlayEffects';
import { resolveProfileThemeEffects, resolveVideoDarken } from '../../../../../utils/memberProfileThemeDisplay';
import { pickPrimaryHandleIdFromHandleList } from '../../../members/personalhub/dashboard/dashboardUtils';
import { fetchRosterFile } from '../../../members/rosterTools/api/rosterFileApi';
import discordAvatarDefault from '../../../../../medias/avatar_default.png';

function pickMasterHandle(handles) {
  const list = Array.isArray(handles) ? handles : [];
  const master = list.find((h) => h && h.isMaster === true) || list[0] || null;
  return master?.handle ? String(master.handle).trim() : '';
}

function displayMonikerFromPlayer(player) {
  if (!player) return '';
  if (player.monikerRedacted) {
    return player.monikerRedactedKind === 'redacted' ? 'Redacted' : 'Hidden';
  }
  return player.rosterMoniker || '';
}

function displayMonikerFromCard(card) {
  if (!card) return '';
  if (card.monikerRedacted) {
    return card.monikerRedactedKind === 'redacted' ? 'Redacted' : 'Hidden';
  }
  if (card.moniker != null && String(card.moniker).trim()) {
    return String(card.moniker).trim();
  }
  return '';
}

export default function ThemeLivePreview({
  draft,
  layout = 'stack',
  showHeader = true,
  className = '',
}) {
  const { t } = useTranslation();
  const { userId, discordName, handles, rankImageUrl, isLoggedIn } = useAuth();
  const [player, setPlayer] = useState(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isRow = layout === 'row';

  useEffect(() => {
    if (!isLoggedIn) {
      setPlayer(null);
      setCard(null);
      setLoading(false);
      return undefined;
    }

    const uid = Number(userId);
    const handleId = pickPrimaryHandleIdFromHandleList(handles);
    let cancelled = false;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const tasks = [];

        if (Number.isInteger(handleId) && handleId > 0) {
          tasks.push(
            fetchRosterFile(handleId).then((data) => {
              if (cancelled) return;
              setPlayer(data?.player || data?.data?.player || null);
            }),
          );
        }

        if (Number.isInteger(uid) && uid > 0) {
          tasks.push(
            fetchMemberProfileCard(uid).then((data) => {
              if (cancelled) return;
              if (!data?.ok) {
                setCard(null);
                return;
              }
              setCard(data.card || null);
            }),
          );
        }

        if (tasks.length === 0) {
          if (!cancelled) setError(t('profileThemePreviewNoIdentity'));
          return;
        }

        await Promise.all(tasks);
      } catch (_) {
        if (!cancelled) setError(t('profileThemePreviewLoadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId, handles, t]);

  const previewTheme = useMemo(() => ({
    effectKey: draft?.effectKey || null,
    overlayEffectKeys: Array.isArray(draft?.overlayEffectKeys) ? draft.overlayEffectKeys : [],
    effectConfig: draft?.effectConfig || null,
    bannerMediaUrl: draft?.bannerMediaType === 'none' ? null : (draft?.bannerMediaUrl || null),
    bannerMediaType: draft?.bannerMediaType === 'none' ? null : (draft?.bannerMediaType || null),
    cardMediaUrl: draft?.cardMediaType === 'none' ? null : (draft?.cardMediaUrl || null),
    cardMediaType: draft?.cardMediaType === 'none' ? null : (draft?.cardMediaType || null),
  }), [draft]);

  const effects = useMemo(
    () => resolveProfileThemeEffects(previewTheme),
    [previewTheme],
  );

  const authHandle = pickMasterHandle(handles);
  const authName = discordName && String(discordName).trim() ? String(discordName).trim() : '';

  const rosterAvatar = player?.discordAvatar || card?.discordAvatar || discordAvatarDefault;
  const rosterDiscordName = player?.rosterDiscordName || card?.discordName || authName || '—';
  const rosterHandle = player?.rosterHandle
    ? `@${player.rosterHandle}`
    : (authHandle ? `@${authHandle}` : '—');
  const rosterMoniker = displayMonikerFromPlayer(player) || displayMonikerFromCard(card) || '—';
  const rosterRankName = player?.rankName || card?.rankName || '';
  const rosterRankImage = player?.rankImageUrl || card?.rankImageUrl || rankImageUrl || '';
  const rosterAssignment = player?.rosterAssignmentDisplay || card?.rosterAssignmentDisplay || '';

  const cardAvatar = card?.discordAvatar || player?.discordAvatar || discordAvatarDefault;
  const cardDiscordName = card?.discordName || player?.rosterDiscordName || authName || '—';
  const cardHandle = card?.primaryHandle
    || card?.handles?.find((h) => h?.isMaster)?.handle
    || card?.handles?.[0]?.handle
    || player?.rosterHandle
    || authHandle
    || '';
  const cardMoniker = displayMonikerFromCard(card) || displayMonikerFromPlayer(player) || '';
  const cardRankName = card?.rankName || player?.rankName || '';
  const cardRankImage = card?.rankImageUrl || player?.rankImageUrl || rankImageUrl || '';
  const cardAssignment = card?.rosterAssignmentDisplay || player?.rosterAssignmentDisplay || '';

  // Match production surfaces: donor/subscriber = static nebula; premium = animated morph.
  const showRosterFx = Boolean(effects.effectKey)
    || Boolean(effects.bannerMediaUrl)
    || effects.overlayEffectKeys.length > 0;
  const showCardFx = Boolean(effects.effectKey)
    || Boolean(effects.cardMediaUrl)
    || effects.overlayEffectKeys.length > 0;
  const rosterOverlays = effects.overlayEffectKeys;
  const cardOverlays = effects.overlayEffectKeys;
  const rosterLegacySpace = Boolean(effects.showDonorSpace);
  const cardLegacySpace = Boolean(effects.showDonorSpace);

  return (
    <section
      className={[
        'profile-themes-preview',
        isRow ? 'profile-themes-preview--row' : '',
        className,
      ].filter(Boolean).join(' ')}
      aria-label={t('profileThemePreviewTitle')}
    >
      {showHeader ? (
        <>
          <h6 className="profile-themes-preview__title">{t('profileThemePreviewTitle')}</h6>
          <p className="profile-themes-preview__hint">{t('profileThemePreviewHint')}</p>
        </>
      ) : null}
      {error ? <p className="profile-themes-preview__error">{error}</p> : null}
      {loading ? (
        <p className="profile-themes-preview__status">{t('profileThemeAdminLoading')}</p>
      ) : null}

      <div
        className={[
          'profile-themes-preview__stack',
          isRow ? 'profile-themes-preview__stack--row' : '',
        ].filter(Boolean).join(' ')}
      >
        {/* Roster header — real roster donor-space classes */}
        <div className="profile-themes-preview__roster">
          <span className="profile-themes-preview__label">{t('profileThemePreviewRoster')}</span>
          <div
            className={[
              'roster-file-summary-hero',
              'profile-themes-preview__roster-shell',
            ].filter(Boolean).join(' ')}
          >
            <PerkThemeHaloLayer effectConfig={effects.effectConfig} />
            <div className="roster-file-summary-hero__banner" aria-hidden>
              {showRosterFx ? (
                <div
                  className={[
                    'roster-file-summary-hero__donor-space',
                    rosterLegacySpace
                      ? ''
                      : 'roster-file-summary-hero__donor-space--plain',
                    effects.isPremiumEffect
                      ? 'roster-file-summary-hero__donor-space--premium'
                      : '',
                  ].filter(Boolean).join(' ')}
                >
                  {rosterOverlays.length ? (
                    <PerkThemeUnderlayEffects
                      effectKeys={rosterOverlays}
                      effectConfig={effects.effectConfig}
                    />
                  ) : null}
                  {effects.bannerMediaUrl ? (
                    <PerkThemeMediaLayer
                      url={effects.bannerMediaUrl}
                      mediaType={effects.bannerMediaType}
                      darken={resolveVideoDarken(
                        effects.bannerMediaUrl,
                        effects.bannerMediaType,
                        effects.effectConfig?.bannerVideoDarken,
                      )}
                      className="roster-file-summary-hero__theme-media"
                    />
                  ) : null}
                  {effects.showNebulaMorph ? (
                    <div className="roster-file-summary-hero__nebula-morph" aria-hidden />
                  ) : null}
                  <div className="roster-file-summary-hero__banner-overlay" />
                  {effects.showShootingStars ? <DonorShootingStar /> : null}
                  {rosterOverlays.length ? (
                    <PerkThemeOverlayEffects
                      effectKeys={rosterOverlays}
                      mediaUrl={effects.bannerMediaUrl}
                      mediaType={effects.bannerMediaType}
                      effectConfig={effects.effectConfig}
                    />
                  ) : null}
                </div>
              ) : (
                <div className="roster-file-summary-hero__banner-overlay" />
              )}
            </div>

            <div className="profile-themes-preview__roster-fg">
              <div className="roster-file-summary__avatar-stack">
                <img
                  className="roster-file-summary__avatar-discord"
                  src={rosterAvatar}
                  alt=""
                />
              </div>
              <div className="profile-themes-preview__identity-text">
                <div className="profile-themes-preview__name">{rosterDiscordName}</div>
                <div className="profile-themes-preview__handle">{rosterHandle}</div>
                <div className="profile-themes-preview__moniker">{rosterMoniker}</div>
                {(rosterRankName || rosterRankImage || rosterAssignment) ? (
                  <div className="profile-themes-preview__roster-meta">
                    {rosterRankImage ? (
                      <img src={rosterRankImage} alt="" className="profile-themes-preview__rank-img" />
                    ) : null}
                    <div>
                      {rosterRankName ? (
                        <div className="profile-themes-preview__rank-name">{rosterRankName}</div>
                      ) : null}
                      {rosterAssignment ? (
                        <div className="profile-themes-preview__assignment">{rosterAssignment}</div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Profile card — real popover donor classes */}
        <div className="profile-themes-preview__card-wrap">
          <span className="profile-themes-preview__label">{t('profileThemePreviewCard')}</span>
          <div
            className={[
              'member-profile-card-popover',
              'profile-themes-preview__card-shell',
              cardLegacySpace ? 'member-profile-card-popover--donor' : '',
              !cardLegacySpace && showCardFx
                ? 'member-profile-card-popover--theme-fx'
                : '',
              effects.isPremiumEffect ? 'member-profile-card-popover--donor-premium' : '',
            ].filter(Boolean).join(' ')}
          >
            <PerkThemeHaloLayer effectConfig={effects.effectConfig} />
            {cardOverlays.length ? (
              <PerkThemeUnderlayEffects
                effectKeys={cardOverlays}
                effectConfig={effects.effectConfig}
              />
            ) : null}
            {effects.cardMediaUrl ? (
              <PerkThemeMediaLayer
                url={effects.cardMediaUrl}
                mediaType={effects.cardMediaType}
                darken={resolveVideoDarken(
                  effects.cardMediaUrl,
                  effects.cardMediaType,
                  effects.effectConfig?.cardVideoDarken,
                )}
                className="member-profile-card-popover__theme-media"
              />
            ) : null}
            {effects.showNebulaMorph ? (
              <div className="member-profile-card-popover__nebula-morph" aria-hidden />
            ) : null}
            {effects.showShootingStars ? <DonorShootingStar /> : null}
            {cardOverlays.length ? (
              <PerkThemeOverlayEffects
                effectKeys={cardOverlays}
                mediaUrl={effects.cardMediaUrl}
                mediaType={effects.cardMediaType}
                effectConfig={effects.effectConfig}
              />
            ) : null}

            <div className="member-profile-card">
              <div className="member-profile-card__hero">
                <div className="member-profile-card__avatar-stack">
                  <img
                    className="member-profile-card__hero-avatar"
                    src={cardAvatar}
                    alt=""
                  />
                </div>
                <div className="member-profile-card__hero-text">
                  <div className="member-profile-card__discord-name">{cardDiscordName}</div>
                  {cardHandle ? (
                    <div className="member-profile-card__handle">{`@${cardHandle}`}</div>
                  ) : null}
                </div>
              </div>
              <div className="member-profile-card__body">
                {cardMoniker ? (
                  <div className="member-profile-card__line">{cardMoniker}</div>
                ) : null}
                {(cardRankName || cardRankImage) ? (
                  <div className="member-profile-card__rank">
                    {cardRankImage ? (
                      <img className="member-profile-card__rank-img" src={cardRankImage} alt="" />
                    ) : null}
                    {cardRankName ? (
                      <span className="member-profile-card__rank-label">{cardRankName}</span>
                    ) : null}
                  </div>
                ) : null}
                {cardAssignment ? (
                  <div className="member-profile-card__line member-profile-card__line--accent">
                    {cardAssignment}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
